import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  Drawer,
  Input,
  message,
  Popconfirm,
  Row,
  Segmented,
  Space,
  Tag,
  theme,
  Tooltip,
  Typography,
} from "antd";
import {
  CloudUploadOutlined,
  RedoOutlined,
  TranslationOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { useAppDispatch } from "../../../app/hooks";
import { IEmailTemplateDetail } from "../../../interfaces/emailTemplate.interface";
import {
  getEmailTemplateThunk,
  publishEmailTemplateThunk,
  saveEmailDraftThunk,
  translateEmailTemplateThunk,
} from "../emailTemplatesSlice";
import {
  LOCALE_LABELS,
  LOCALE_ORDER,
  sampleParams,
} from "../emailTemplatesHelpers";
import {
  Drafts,
  EMPTY_DRAFT,
  LocaleDraft,
  toContent,
  toEditorDraft,
} from "../editorContent";
import { useDraftHistory } from "../hooks/useDraftHistory";
import BlockList from "./BlockList";
import EmailPreview from "./EmailPreview";

const { Text } = Typography;
const { TextArea } = Input;

const canPublish = process.env.REACT_APP_STAGE === "prod";

interface EmailTemplateEditorProps {
  open: boolean;
  slug: string | null;
  onClose: () => void;
}

const EmailTemplateEditor: React.FC<EmailTemplateEditorProps> = ({
  open,
  slug,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const { token } = theme.useToken();
  const { drafts, canUndo, canRedo, load, change, undo, redo } =
    useDraftHistory();

  const [detail, setDetail] = useState<IEmailTemplateDetail | null>(null);
  const [locale, setLocale] = useState<string>("en");
  const [paramsText, setParamsText] = useState("{}");
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const hydrate = useCallback(
    (loaded: IEmailTemplateDetail) => {
      setDetail(loaded);
      const next: Drafts = {};
      loaded.locales.forEach((loc) => {
        next[loc] = toEditorDraft(loaded.draft[loc] ?? loaded.published[loc]);
      });
      load(next);
      setParamsText(JSON.stringify(sampleParams(loaded.params), null, 2));
    },
    [load]
  );

  useEffect(() => {
    if (!open || !slug) return;
    setLocale("en");
    dispatch(getEmailTemplateThunk(slug))
      .unwrap()
      .then((loaded) => {
        hydrate(loaded);
        if (!loaded.locales.includes("en") && loaded.locales[0]) {
          setLocale(loaded.locales[0]);
        }
      })
      .catch(() => message.error("Failed to load template."));
  }, [open, slug, dispatch, hydrate]);

  const draft = drafts[locale] ?? EMPTY_DRAFT;

  const patchDraft = (partial: Partial<LocaleDraft>) =>
    change({
      ...drafts,
      [locale]: { ...(drafts[locale] ?? EMPTY_DRAFT), ...partial },
    });

  const previewData = useMemo(() => {
    try {
      return JSON.parse(paramsText || "{}") as Record<string, unknown>;
    } catch {
      return {};
    }
  }, [paramsText]);

  const targetLocales = useMemo(
    () => LOCALE_ORDER.filter((loc) => loc !== locale),
    [locale]
  );

  const handleSave = async () => {
    if (!slug) return;
    setSaving(true);
    try {
      const updated = await dispatch(
        saveEmailDraftThunk({
          slug,
          payload: { locale, content: toContent(draft) },
        })
      ).unwrap();
      hydrate(updated);
      message.success(`Draft saved (${LOCALE_LABELS[locale] ?? locale}).`);
    } catch {
      message.error("Failed to save draft.");
    } finally {
      setSaving(false);
    }
  };

  const handleTranslate = async () => {
    if (!slug || !targetLocales.length) return;
    setTranslating(true);
    try {
      await dispatch(
        saveEmailDraftThunk({
          slug,
          payload: { locale, content: toContent(draft) },
        })
      ).unwrap();
      const updated = await dispatch(
        translateEmailTemplateThunk({
          slug,
          payload: { sourceLocale: locale, targetLocales },
        })
      ).unwrap();
      hydrate(updated);
      message.success("Translated into other locales (saved as drafts).");
    } catch {
      message.error("Failed to translate.");
    } finally {
      setTranslating(false);
    }
  };

  const handlePublish = async () => {
    if (!slug || !detail) return;
    setPublishing(true);
    try {
      await dispatch(
        saveEmailDraftThunk({
          slug,
          payload: { locale, content: toContent(draft) },
        })
      ).unwrap();
      const updated = await dispatch(
        publishEmailTemplateThunk({
          slug,
          payload: { locales: detail.locales },
        })
      ).unwrap();
      hydrate(updated);
      message.success("Published to live.");
    } catch {
      message.error("Publish failed (only allowed in prod).");
    } finally {
      setPublishing(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="94%"
      title={slug ? `Edit: ${slug}` : "Email Template"}
      extra={
        <Space>
          <Tooltip title="Undo">
            <Button
              icon={<UndoOutlined />}
              disabled={!canUndo}
              onClick={undo}
            />
          </Tooltip>
          <Tooltip title="Redo">
            <Button
              icon={<RedoOutlined />}
              disabled={!canRedo}
              onClick={redo}
            />
          </Tooltip>
          <Button
            icon={<TranslationOutlined />}
            loading={translating}
            disabled={!targetLocales.length}
            onClick={handleTranslate}
          >
            Auto-translate
          </Button>
          <Button loading={saving} onClick={handleSave}>
            Save Draft
          </Button>
          <Tooltip
            title={canPublish ? "" : "Publishing is only allowed in prod"}
          >
            <Popconfirm
              title="Publish all locales to live?"
              description="This updates the emails sent across all environments."
              okText="Publish"
              disabled={!canPublish}
              onConfirm={handlePublish}
            >
              <Button
                type="primary"
                icon={<CloudUploadOutlined />}
                loading={publishing}
                disabled={!canPublish}
              >
                Publish
              </Button>
            </Popconfirm>
          </Tooltip>
        </Space>
      }
    >
      <Row gutter={24}>
        <Col xs={24} md={14} lg={15}>
          <Space wrap style={{ marginBottom: token.margin }}>
            <Segmented
              value={locale}
              onChange={(val) => setLocale(val as string)}
              options={LOCALE_ORDER.map((loc) => ({
                label: detail?.locales.includes(loc)
                  ? (LOCALE_LABELS[loc] ?? loc)
                  : `${LOCALE_LABELS[loc] ?? loc} (new)`,
                value: loc,
              }))}
            />
            {detail && <Tag color="processing">v{detail.publishedVersion}</Tag>}
          </Space>

          <div style={{ marginBottom: token.margin }}>
            <Text type="secondary">SUBJECT</Text>
            <Input
              style={{ marginTop: 4 }}
              placeholder="Email subject — supports {{vars}}"
              value={draft.subject}
              onChange={(e) => patchDraft({ subject: e.target.value })}
            />
          </div>

          <Text type="secondary">CONTENT BLOCKS</Text>
          <div style={{ marginTop: token.marginXS }}>
            <BlockList
              blocks={draft.blocks}
              onChange={(blocks) => patchDraft({ blocks })}
            />
          </div>

          <div style={{ marginTop: token.marginLG }}>
            <Text type="secondary">PREVIEW DATA (JSON)</Text>
            <TextArea
              style={{ marginTop: 4, fontFamily: "monospace" }}
              autoSize={{ minRows: 3, maxRows: 10 }}
              value={paramsText}
              onChange={(e) => setParamsText(e.target.value)}
            />
          </div>
        </Col>

        <Col xs={24} md={10} lg={9}>
          {slug && (
            <EmailPreview
              slug={slug}
              locale={locale}
              content={toContent(draft)}
              data={previewData}
            />
          )}
        </Col>
      </Row>
    </Drawer>
  );
};

export default EmailTemplateEditor;
